/**
 * Maersk Schedules API Client
 *
 * Shared API client for Maersk Schedules API using port-calls endpoint.
 * Provides consistent interface for all services to fetch schedule data.
 *
 * @module services/DcsaApiClient
 */

// Requests go through our own backend (/api/dcsa), which attaches the
// Consumer-Key server-side. The key never reaches the browser.
const API_BASE_URL = '/api/dcsa';

// Simple in-memory cache to prevent duplicate calls (e.g., from React StrictMode)
const pendingRequests = new Map();
const CACHE_TTL = 60000; // 60 seconds (increased from 5 to reduce API calls)

// API strategy: 'hybrid' = service code + IMO fallback, 'ports' = port-schedules only
// Use 'ports' to avoid rate limiting (3 API calls vs 10+)
const API_STRATEGY = 'hybrid'; // Change to 'ports' for fewer API calls

/**
 * Delay helper for rate limiting
 */
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Rate-limited fetch with automatic retry logic
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options
 * @param {number} retryCount - Current retry attempt
 * @returns {Promise<Response>} - Fetch response
 */
const rateLimitedFetch = async (url, options = {}, retryCount = 3) => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Accept': 'application/json',
        ...options?.headers
      }
    });

    if (response.status === 429 && retryCount > 0) {
      const waitTime = 10000; // 10 seconds for rate limit retry (increased from 5)
      console.log(`Rate limit hit, waiting ${waitTime/1000}s before retry... (retries left: ${retryCount})`);
      await delay(waitTime);
      return rateLimitedFetch(url, options, retryCount - 1);
    }

    if (!response.ok) {
      throw new Error(`API error: ${response.status} - ${await response.text()}`);
    }

    return response.json();
  } catch (error) {
    console.error('Fetch error:', error);
    if (retryCount > 0) {
      await delay(2000);
      return rateLimitedFetch(url, options, retryCount - 1);
    }
    throw error;
  }
};

/**
 * Get date range for API queries
 * @param {number} daysBack - Days to look back from today (default: 31)
 * @param {number} weeksForward - Weeks to look forward from today (default: 24)
 * @returns {object} - { startDate, dateRange }
 */
export const getDateRange = (daysBack = 40, daysForward = 90) => {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - daysBack);

  const endDate = new Date(today);
  endDate.setDate(today.getDate() + daysForward);

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    dateRange: 'current'
  };
};

/**
 * Find best timestamp by priority: ACT > EST > PLN
 * @param {Array} timestamps - Array of timestamp objects from API
 * @param {string} eventType - Event type to find ('ARRI' or 'DEPA')
 * @returns {object|null} - Best timestamp or null
 */
export const findBestTimestamp = (timestamps, eventType) => {
  if (!timestamps || !Array.isArray(timestamps)) return null;

  // Priority: ACT > EST > PLN
  const priorities = ['ACT', 'EST', 'PLN'];

  for (const priority of priorities) {
    const result = timestamps.find(ts =>
      ts.eventTypeCode === eventType && ts.eventClassifierCode === priority
    );
    if (result) return result;
  }

  return null;
};

/**
 * Format date string to display format
 * @param {string} dateTimeStr - ISO date string
 * @param {string} eventClassifierCode - 'ACT' or 'EST'
 * @returns {object} - { text, type, rawDateTime }
 */
export const formatDate = (dateTimeStr, eventClassifierCode) => {
  if (!dateTimeStr || dateTimeStr === 'N/A' || dateTimeStr === 'TBD') {
    return { text: 'N/A', type: 'EST', rawDateTime: null };
  }

  try {
    const date = new Date(dateTimeStr);
    if (isNaN(date.getTime())) {
      console.warn(`[formatDate] Invalid Date created from string: ${dateTimeStr}`);
      return { text: 'Invalid Date', type: 'EST', rawDateTime: dateTimeStr };
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);

    const result = {
      text: `${day} ${month} ${year}`,
      type: eventClassifierCode === 'ACT' ? 'ACT' : 'EST',
      rawDateTime: dateTimeStr
    };

    // Debug logging for date classification
    if (eventClassifierCode === 'ACT') {
      console.log(`[formatDate] ✅ ACT DATE FOUND: ${dateTimeStr} -> ${result.text}`);
    } else if (eventClassifierCode === 'EST') {
      console.log(`[formatDate] ⏰ EST DATE: ${dateTimeStr} -> ${result.text}`);
    } else {
      console.log(`[formatDate] ❓ UNKNOWN TYPE (${eventClassifierCode}): ${dateTimeStr} -> ${result.text}`);
    }

    return result;
  } catch (error) {
    console.error('Error formatting date:', dateTimeStr, error);
    return { text: 'Error', type: 'EST', rawDateTime: dateTimeStr };
  }
};

/**
 * Filter out vessels whose entire schedule is in the past
 * For export vessels, check both SA port calls AND discharge port calls
 * @param {Array} consolidatedVessels - Array of vessel data objects
 * @param {Array} loadPortCodes - Load port codes to identify discharge ports
 * @returns {Array} - Filtered vessels with future activity
 */
export const filterStaleVessels = (consolidatedVessels, loadPortCodes) => {
  const now = new Date();
  console.log(`[filterStaleVessels] Current time (now): ${now.toISOString()}`);

  return consolidatedVessels.filter(v => {
    // Check if vessel has any SA port calls with future timestamps
    const hasFutureSAPort = v.saPortCalls?.some(pc => {
      const pcDate = new Date(pc.dateTime);
      return pcDate > now;
    });

    // Check if vessel has any discharge port calls with future timestamps
    const hasFutureDischargePort = v.allTransportCalls?.some(tc => {
      const isDischargePort = !loadPortCodes.includes(tc.location?.UNLocationCode);
      if (!isDischargePort) return false;

      const timestamps = tc?.timestamps || [];
      return timestamps.some(ts => {
        const eventDate = new Date(ts.eventDateTime);
        return eventDate > now;
      });
    });

    const hasFuture = hasFutureSAPort || hasFutureDischargePort;

    if (!hasFuture && v.vessel.name.includes('SANTA')) {
      console.log(`[filterStaleVessels] ${v.vessel.name} ${v.vessel.voyage}: All activity in the past (filtered out)`);
    }

    return hasFuture;
  });
};

/**
 * Build load port schedules for a rotation of vessels
 * @param {Array} rotation - Array of vessel data with saPortCalls
 * @param {Array} loadPortConfigs - Load port configurations
 * @returns {Array} - Array of port objects with schedules
 */
export const buildLoadPorts = (rotation, loadPortConfigs) => {
  return loadPortConfigs.map(portConfig => {
    const schedules = rotation.map(vesselData => {
      // Debug: Show saPortCalls for first vessel
      if (portConfig.portCode === 'ZADUR' && vesselData.vessel.name.includes('SANTA')) {
        console.log(`[buildLoadPorts] DEBUG ${vesselData.vessel.name} saPortCalls:`, vesselData.saPortCalls?.slice(0, 5));
      }

      // Find matching port calls in SA port calls
      const portCalls = vesselData.saPortCalls?.filter(call => call.port === portConfig.portCode);

      if (!portCalls || portCalls.length === 0) {
        return {
          eta: { text: 'N/A', type: 'EST' },
          etd: { text: 'N/A', type: 'EST' }
        };
      }

      // For Port Elizabeth, use the last DEPA port call
      // For other ports, prioritize ACT over EST over PLN for ARRI calls
      let selectedPortCall;
      if (portConfig.portCode === 'ZAPLZ') {
        const depaCalls = portCalls.filter(c => c.type === 'DEPA');
        selectedPortCall = depaCalls.length > 0 ? depaCalls[depaCalls.length - 1] : null;
      } else {
        // Prioritize ACT over EST over PLN for arrival calls
        const arriCalls = portCalls.filter(c => c.type === 'ARRI');
        const actArr = arriCalls.find(c => c.status === 'ACT');
        const estArr = arriCalls.find(c => c.status === 'EST');
        const plnArr = arriCalls.find(c => c.status === 'PLN');
        selectedPortCall = actArr || estArr || plnArr || arriCalls[0] || null;
      }

      if (!selectedPortCall) {
        // No matching port call type found, try to find any arrival/departure
        const arr = portCalls.find(c => c.type === 'ARRI');
        const dep = portCalls.find(c => c.type === 'DEPA');
        return {
          eta: formatDate(arr?.dateTime, arr?.status),
          etd: formatDate(dep?.dateTime, dep?.status)
        };
      }

      // Find arrival and departure for the same transport call sequence
      // Prioritize ACT over EST over PLN within the same sequence
      const tcSequence = selectedPortCall.transportCallSequence || 0;

      // Find all arrivals/departures with matching sequence, then prioritize by status
      const matchingArr = portCalls.filter(c => c.type === 'ARRI' && c.transportCallSequence === tcSequence);
      const matchingDep = portCalls.filter(c => c.type === 'DEPA' && c.transportCallSequence === tcSequence);

      const arr = matchingArr.find(c => c.status === 'ACT') ||
                  matchingArr.find(c => c.status === 'EST') ||
                  matchingArr.find(c => c.status === 'PLN') ||
                  matchingArr[0] || null;

      const dep = matchingDep.find(c => c.status === 'ACT') ||
                  matchingDep.find(c => c.status === 'EST') ||
                  matchingDep.find(c => c.status === 'PLN') ||
                  matchingDep[0] || null;

      // If not found by sequence, use fallback logic with ACT priority
      const allArrCalls = portCalls.filter(c => c.type === 'ARRI');
      const allDepCalls = portCalls.filter(c => c.type === 'DEPA');

      const fallbackArr = allArrCalls.find(c => c.status === 'ACT') ||
                         allArrCalls.find(c => c.status === 'EST') ||
                         allArrCalls.find(c => c.status === 'PLN') ||
                         allArrCalls[0] || null;

      const fallbackDep = allDepCalls.find(c => c.status === 'ACT') ||
                         allDepCalls.find(c => c.status === 'EST') ||
                         allDepCalls.find(c => c.status === 'PLN') ||
                         allDepCalls[0] || null;

      // Use the found arr/dep, or fallback if not found
      const finalArr = arr || fallbackArr;
      const finalDep = dep || fallbackDep;

      // Extract timestamps array from any matching port call for performance calculation
      const matchingCallWithTimestamps = portCalls.find(pc => pc.timestamps && pc.timestamps.length > 0);
      const timestamps = matchingCallWithTimestamps?.timestamps || [];

      const result = {
        eta: formatDate(finalArr?.dateTime, finalArr?.status),
        etd: formatDate(finalDep?.dateTime, finalDep?.status),
        timestamps: timestamps // PRESERVE: Include raw timestamps for performance calculation
      };

      // Debug: Log all vessels for Durban to check ACT status
      if (portConfig.portCode === 'ZADUR') {
        console.log(`[buildLoadPorts] ${vesselData.vessel.name} ${vesselData.vessel.voyage} at ${portConfig.name}:`, result);
        console.log(`[buildLoadPorts]   arr: dateTime=${finalArr?.dateTime}, status=${finalArr?.status}`);
        console.log(`[buildLoadPorts]   dep: dateTime=${finalDep?.dateTime}, status=${finalDep?.status}`);
        console.log(`[buildLoadPorts]   formatted: eta.type=${result.eta?.type}, etd.type=${result.etd?.type}`);
      }

      // Debug: Log first few vessels for first port
      if (portConfig.name === 'Port Elizabeth' && vesselData.vessel.name.includes('SANTA')) {
        console.log(`[buildLoadPorts] ${vesselData.vessel.name} ${vesselData.vessel.voyage} at ${portConfig.name}:`, result);
        console.log(`[buildLoadPorts]   arr: status=${finalArr?.status}, type=${finalArr?.type}`);
        console.log(`[buildLoadPorts]   dep: status=${finalDep?.status}, type=${finalDep?.type}`);
      }

      return result;
    });

    return {
      name: portConfig.name,
      portCode: portConfig.portCode,
      schedules
    };
  });
};

/**
 * Build discharge port schedules for a rotation of vessels
 * @param {Array} rotation - Array of vessel data with allTransportCalls
 * @param {Array} dischargePortConfigs - Discharge port configurations
 * @returns {Array} - Array of port objects with schedules
 */
export const buildDischargePorts = (rotation, dischargePortConfigs) => {
  return dischargePortConfigs.map(portConfig => {
    const schedules = rotation.map(vesselData => {
      // Find matching transport call by UNLocationCode and optional facilitySMDGCode
      const call = (vesselData.allTransportCalls || []).find(tc => {
        if (tc.location?.UNLocationCode !== portConfig.portCode) return false;
        if (portConfig.facilitySMDGCode && tc.location?.facilitySMDGCode !== portConfig.facilitySMDGCode) {
          return false;
        }
        return true;
      });

      if (!call) {
        return { eta: { text: 'N/A', type: 'EST' }, etd: { text: 'N/A', type: 'EST' } };
      }

      const arr = findBestTimestamp(call.timestamps, 'ARRI');
      const dep = findBestTimestamp(call.timestamps, 'DEPA');

      // Debug logging for timestamps
      console.log(`[buildLoadPorts] ${portConfig.name} raw timestamps:`, {
        arr: arr ? {
          eventDateTime: arr.eventDateTime,
          eventClassifierCode: arr.eventClassifierCode,
          eventTypeCode: arr.eventTypeCode
        } : null,
        dep: dep ? {
          eventDateTime: dep.eventDateTime,
          eventClassifierCode: dep.eventClassifierCode,
          eventTypeCode: dep.eventTypeCode
        } : null
      });

      return {
        eta: formatDate(arr?.eventDateTime, arr?.eventClassifierCode),
        etd: formatDate(dep?.eventDateTime, dep?.eventClassifierCode),
        timestamps: call.timestamps || [] // PRESERVE: Include raw timestamps for performance calculation
      };
    });

    return {
      name: portConfig.name,
      portCode: portConfig.portCode,
      schedules
    };
  });
};

/**
 * Standardized vessel object for ScheduleGrid
 * @param {object} vessel - Raw vessel data
 * @returns {object} - Standardized vessel object
 */
export const standardizeVessel = (vessel) => {
  return {
    imo: vessel.vesselIMONumber || vessel.vesselIMO || vessel.imo || '',
    name: vessel.name || vessel.vesselName || '',
    voyage: vessel.voyage || vessel.voyageNumber || ''
  };
};

/**
 * Transform DCSA service-schedules API response to standard schedule format
 * @param {Array} serviceSchedules - Array of ServiceSchedule objects from DCSA API
 * @param {string} serviceCode - Service code for filtering
 * @param {number} vesselsPerGrid - Number of vessels per grid (default: 8)
 * @param {Array} dischargePortConfigs - Discharge port configurations for the service
 * @returns {object} - { current, upcoming } with standard format
 */
export const transformServiceScheduleData = (serviceSchedules, serviceCode, vesselsPerGrid = 8, dischargePortConfigs = null, loadPortConfigs = null) => {
  // Get service-specific configurations
  const finalDischargeConfigs = dischargePortConfigs || getDischargePortConfigs(serviceCode);
  const finalLoadConfigs = loadPortConfigs || getLoadPortConfigs(serviceCode);
  const finalLoadCodes = getLoadPortCodes(serviceCode);

  console.log(`*** TRANSFORM START *** serviceCode: ${serviceCode}, schedules: ${serviceSchedules?.length || 0}`);
  console.log(`[transformServiceScheduleData] Starting transformation for serviceCode: ${serviceCode}`);
  console.log(`[transformServiceScheduleData] Load ports: ${finalLoadCodes.join(', ')}`);
  console.log(`[transformServiceScheduleData] Received ${serviceSchedules?.length || 0} service schedules`);

  const vesselMap = new Map();

  // DCSA API returns an array of ServiceSchedule objects
  if (!Array.isArray(serviceSchedules)) {
    console.error('[transformServiceScheduleData] Expected array of service schedules, got:', typeof serviceSchedules);
    return { current: { vessels: [], loadPorts: [], dischargePorts: [] }, upcoming: { vessels: [], loadPorts: [], dischargePorts: [] } };
  }

  serviceSchedules.forEach(serviceSchedule => {
    console.log(`[transformServiceScheduleData] Received service: ${serviceSchedule.carrierServiceName} (${serviceSchedule.carrierServiceCode}) - queried: ${serviceCode}`);

    // Verify service code matches
    if (serviceSchedule.carrierServiceCode !== serviceCode) {
      console.log(`[transformServiceScheduleData] Skipping service ${serviceSchedule.carrierServiceName} (${serviceSchedule.carrierServiceCode}) - not matching ${serviceCode}`);
      return;
    }

    const vesselSchedules = serviceSchedule?.vesselSchedules || [];
    console.log(`[transformServiceScheduleData] Processing ${vesselSchedules.length} vessel schedules for service ${serviceSchedule.carrierServiceName}`);

    vesselSchedules.forEach(vesselSchedule => {
      const vesselIMONumber = vesselSchedule?.vessel?.vesselIMONumber;
      const vesselName = vesselSchedule?.vessel?.name; // Changed from vesselName to name

      const transportCalls = vesselSchedule?.transportCalls || [];

      if (!vesselIMONumber || !vesselName) {
        console.log(`[transformServiceScheduleData] Skipping vessel schedule - missing IMO or name:`, {
          vessel: vesselSchedule?.vessel,
          imo: vesselIMONumber,
          name: vesselName
        });
        return;
      }

      if (transportCalls.length === 0) {
        console.log(`[transformServiceScheduleData] Skipping ${vesselName} - no transport calls`);
        return;
      }

      // For export schedule: find voyages where vessel calls SA ports
      // Get export voyage numbers from SA port transport calls only
      const exportVoyages = new Set();

      transportCalls.forEach(tc => {
        const unLocationCode = tc?.location?.UNLocationCode;
        const isSAPort = finalLoadCodes.includes(unLocationCode);

        if (isSAPort) {
          const exportVoyage = tc?.carrierExportVoyageNumber;
          // Include export voyages (ending in 'N' for North, 'E' for East), not import voyages (ending in 'S', 'W')
          if (exportVoyage && (exportVoyage.endsWith('N') || exportVoyage.endsWith('E'))) {
            exportVoyages.add(exportVoyage);
          }
        }
      });

      console.log(`[transformServiceScheduleData] ${vesselName}: exportVoyagesFromSAPorts=[${Array.from(exportVoyages).join(', ')}]`);

      if (exportVoyages.size === 0) {
        console.log(`[transformServiceScheduleData] Skipping ${vesselName} - no export voyages from SA ports found`);
        return;
      }

      // Create a separate entry for EACH export voyage
      for (const voyageNumber of exportVoyages) {
        const vesselKey = `${vesselName}-${voyageNumber}`;

        console.log(`[transformServiceScheduleData] Processing vessel: ${vesselKey}, IMO: ${vesselIMONumber}, ${transportCalls.length} total transport calls`);

        // Find the connecting import voyage (e.g., 617S connects to 621N)
        // SA ports have importVoyage !== exportVoyage during the transition
        const connectingImportVoyage = (() => {
          for (const tc of transportCalls) {
            const unLocationCode = tc?.location?.UNLocationCode;
            const isSAPort = finalLoadCodes.includes(unLocationCode);
            const exportVoyage = tc?.carrierExportVoyageNumber;
            const importVoyage = tc?.carrierImportVoyageNumber;

            if (isSAPort && exportVoyage === voyageNumber && importVoyage && importVoyage !== voyageNumber) {
              return importVoyage;
            }
          }
          return null;
        })();

        console.log(`[transformServiceScheduleData] ${vesselKey}: connectingImportVoyage=${connectingImportVoyage}`);

        // Debug: Log all transport calls for SANTA URSULA
        if (vesselName.includes('SANTA URSULA')) {
          console.log(`[transformServiceScheduleData] ${vesselKey}: ALL ${transportCalls.length} transport calls:`);
          const uniquePorts = [...new Set(transportCalls.map(tc => tc.location?.UNLocationCode).filter(Boolean))];
          console.log(`  Unique ports in API: [${uniquePorts.join(', ')}]`);

          // Check specifically for ZAPLZ
          const zaplzCalls = transportCalls.filter(tc => tc.location?.UNLocationCode === 'ZAPLZ');
          console.log(`  ZAPLZ calls: ${zaplzCalls.length} found`);
          zaplzCalls.forEach(tc => {
            console.log(`    ZAPLZ: exportVoyage=${tc.carrierExportVoyageNumber}, importVoyage=${tc.carrierImportVoyageNumber}`);
          });

          // Check ZAZBA (Coega)
          const zazbaCalls = transportCalls.filter(tc => tc.location?.UNLocationCode === 'ZAZBA');
          console.log(`  ZAZBA (Coega) calls: ${zazbaCalls.length} found`);

          // Check ZADUR, ZACPT
          const zadurCalls = transportCalls.filter(tc => tc.location?.UNLocationCode === 'ZADUR');
          const zacptCalls = transportCalls.filter(tc => tc.location?.UNLocationCode === 'ZACPT');
          console.log(`  ZADUR calls: ${zadurCalls.length} found, ZACPT calls: ${zacptCalls.length} found`);

          // Check discharge ports
          const dischargeCalls = transportCalls.filter(tc => !finalLoadCodes.includes(tc.location?.UNLocationCode) && tc.location?.UNLocationCode);
          console.log(`  Discharge port calls: ${dischargeCalls.length} found`);
          dischargeCalls.forEach(tc => {
            console.log(`    ${tc.location?.UNLocationCode}: exportVoyage=${tc.carrierExportVoyageNumber}, importVoyage=${tc.carrierImportVoyageNumber}`);
          });
        }

        // Filter transport calls to get the right data for each voyage
        // For export voyage 617N:
        //   - Export leg (617N) has all ports from CPT onwards
        //   - Import leg (613S) has ZAPLZ before the transition
        // We need both, but discharge ports must come from export leg only
        const voyageTransportCalls = transportCalls.filter(tc => {
          const exportVoyage = tc?.carrierExportVoyageNumber;
          const importVoyage = tc?.carrierImportVoyageNumber;
          const unLocationCode = tc?.location?.UNLocationCode;
          const isSAPort = finalLoadCodes.includes(unLocationCode);

          // Debug logging for SANTA URSULA
          if (vesselName.includes('SANTA URSULA')) {
            console.log(`[filter] ${unLocationCode}: exportVoyage=${exportVoyage}, importVoyage=${importVoyage}, target=${voyageNumber}`);
          }

          // Rule 1: Always include transport calls from this export voyage (for all ports)
          if (exportVoyage === voyageNumber) {
            return true;
          }

          // Rule 2: For SA ports ONLY, also include import leg calls that connect
          // This catches SA ports from the connecting import leg
          if (isSAPort && connectingImportVoyage) {
            // Match by export voyage (SA ports on previous leg)
            if (exportVoyage === connectingImportVoyage) {
              return true;
            }
            // Also match by import voyage (alternative pattern)
            if (importVoyage === connectingImportVoyage) {
              return true;
            }
          }

          // Rule 3: For discharge ports, include if importVoyage matches our target
          // This catches LGP/RTM/BRV where importVoyage=617N but exportVoyage=623S (return leg)
          // These discharge ports are part of the 617N voyage even though their exportVoyage is 623S
          if (!isSAPort && importVoyage === voyageNumber) {
            return true;
          }

          return false;
        });

        console.log(`[transformServiceScheduleData] ${vesselKey}: ${voyageTransportCalls.length} transport calls for voyage ${voyageNumber} (filtered from ${transportCalls.length} total)`);

        // Debug: Log discharge port transport calls for SANTA URSULA
        if (vesselName.includes('SANTA URSULA')) {
          const dischargeCalls = voyageTransportCalls.filter(tc => !finalLoadCodes.includes(tc?.location?.UNLocationCode));
          console.log(`[transformServiceScheduleData] ${vesselKey}: ${dischargeCalls.length} discharge port calls:`);
          dischargeCalls.forEach(dc => {
            console.log(`  ${dc.location?.UNLocationCode}: exportVoyage=${dc.carrierExportVoyageNumber}, importVoyage=${dc.carrierImportVoyageNumber}`);
          });
        }

        // Extract SA port calls and store all transport calls (filtered by voyage)
        const saPortCalls = [];
        const allTransportCalls = [];

        voyageTransportCalls.forEach(transportCall => {
          const unLocationCode = transportCall?.location?.UNLocationCode;
          const isSAPort = finalLoadCodes.includes(unLocationCode);

          // Extract timestamps for SA ports
          if (isSAPort) {
            const timestamps = transportCall?.timestamps || [];
            const portCallSequence = transportCall.transportCallSequence || 0;

            timestamps.forEach(timestamp => {
              saPortCalls.push({
                port: unLocationCode,
                type: timestamp.eventTypeCode, // 'ARRI' or 'DEPA'
                status: timestamp.eventClassifierCode, // 'ACT', 'EST', 'PLN'
                dateTime: timestamp.eventDateTime,
                transportCallSequence: portCallSequence,
                timestamps: timestamps // PRESERVE: Include full timestamps array for this transport call
              });
            });
          }

          // Store all transport calls for discharge port matching
          allTransportCalls.push(transportCall);
        });

        console.log(`[transformServiceScheduleData] ${vesselKey}: ${saPortCalls.length} SA port calls, ${allTransportCalls.length} total transport calls`);

        // Debug: Check SA port calls for ACT status (for SANTA URSULA)
        if (vesselName.includes('SANTA URSULA')) {
          const actCalls = saPortCalls.filter(pc => pc.status === 'ACT');
          console.log(`[transformServiceScheduleData] ${vesselKey}: ${actCalls.length} ACT calls out of ${saPortCalls.length} total`);
          if (actCalls.length > 0) {
            actCalls.forEach(pc => {
              console.log(`  ${pc.port} ${pc.type}: ${pc.dateTime.substring(0, 10)} (${pc.status})`);
            });
          }
        }

        if (!vesselMap.has(vesselKey)) {
          vesselMap.set(vesselKey, {
            vessel: {
              name: vesselName,
              voyage: voyageNumber,
              code: vesselSchedule?.vessel?.operatorCarrierCode,
              vesselIMONumber: vesselIMONumber
            },
            saPortCalls: saPortCalls,
            allTransportCalls: allTransportCalls
          });

          console.log(`[transformServiceScheduleData] ✅ Added vessel: ${vesselKey} with ${saPortCalls.length} SA port calls and ${allTransportCalls.length} total transport calls`);
        } else {
          // Merge SA port calls if vessel already exists
          const existing = vesselMap.get(vesselKey);
          existing.saPortCalls.push(...saPortCalls);
          existing.allTransportCalls.push(...allTransportCalls);
          console.log(`[transformServiceScheduleData] 🔄 Updated vessel: ${vesselKey} (merged ${saPortCalls.length} SA calls, ${allTransportCalls.length} transport calls)`);
        }
      }
    });
  });

  // Filter out vessels with no SA port calls
  const consolidatedVessels = Array.from(vesselMap.values());
  console.log(`[transformServiceScheduleData] Total vessels found: ${consolidatedVessels.length}`);

  // Filter out stale vessels
  const activeVessels = filterStaleVessels(consolidatedVessels, finalLoadCodes);
  console.log(`[transformServiceScheduleData] Active vessels after filtering: ${activeVessels.length}`);

  // Sort vessels by voyage number
  const sortedVessels = activeVessels.sort((a, b) => {
    const voyageNumA = parseInt(a.vessel.voyage?.match(/\d+/)?.[0] || '0', 10);
    const voyageNumB = parseInt(b.vessel.voyage?.match(/\d+/)?.[0] || '0', 10);
    if (voyageNumA !== voyageNumB) {
      return voyageNumA - voyageNumB;
    }
    // Sort by latest SA departure time
    const depA = a.saPortCalls?.filter(c => c.type === 'DEPA').pop();
    const depB = b.saPortCalls?.filter(c => c.type === 'DEPA').pop();
    const timeA = depA ? new Date(depA.dateTime) : new Date('9999');
    const timeB = depB ? new Date(depB.dateTime) : new Date('9999');
    return timeA - timeB;
  });

  console.log('[transformServiceScheduleData] Sorted vessels (first 5):');
  sortedVessels.slice(0, 5).forEach((v, idx) => {
    console.log(`  ${idx}: ${v.vessel.name} ${v.vessel.voyage} (${v.saPortCalls?.length || 0} SA calls, ${v.allTransportCalls?.length || 0} total calls)`);
  });

  // Split into current and upcoming rotations
  const currentRotation = sortedVessels.slice(0, vesselsPerGrid);
  const upcomingRotation = sortedVessels.slice(vesselsPerGrid, vesselsPerGrid * 2);

  const result = {
    current: {
      vessels: currentRotation.map(v => standardizeVessel(v.vessel)),
      loadPorts: buildLoadPorts(currentRotation, finalLoadConfigs),
      dischargePorts: buildDischargePorts(currentRotation, finalDischargeConfigs)
    },
    upcoming: {
      vessels: upcomingRotation.map(v => standardizeVessel(v.vessel)),
      loadPorts: buildLoadPorts(upcomingRotation, finalLoadConfigs),
      dischargePorts: buildDischargePorts(upcomingRotation, finalDischargeConfigs)
    }
  };

  console.log('[transformServiceScheduleData] RESULT - Current vessels:', result.current.vessels.map(v => `${v.name} ${v.voyage}`));
  console.log('[transformServiceScheduleData] RESULT - Upcoming vessels:', result.upcoming.vessels.map(v => `${v.name} ${v.voyage}`));

  return result;
};

/**
 * Transform port-calls API response to standard schedule format (deprecated - use transformServiceScheduleData)
 * @param {object} allSAPortData - Port calls data for SA ports
 * @param {string} serviceCode - Service code for filtering
 * @param {number} vesselsPerGrid - Number of vessels per grid (default: 8)
 * @param {Array} dischargePortConfigs - Discharge port configurations for the service
 * @returns {object} - { current, upcoming } with standard format
 */
export const transformPortCallsData = (allSAPortData, serviceCode, vesselsPerGrid = 8, dischargePortConfigs = null) => {
  // Get service-specific configurations
  const finalDischargeConfigs = dischargePortConfigs || getDischargePortConfigs(serviceCode);
  const finalLoadConfigs = getLoadPortConfigs(serviceCode);
  const finalLoadCodes = getLoadPortCodes(serviceCode);

  console.log(`[transformPortCallsData] Starting transformation for serviceCode: ${serviceCode}`);
  console.log(`[transformPortCallsData] Input data:`, {
    capeTownData: allSAPortData.ZACPT,
    durbanData: allSAPortData.ZADUR,
    peData: allSAPortData.ZAPLZ
  });

  const vesselMap = new Map();

  // Process all port call data from API response
  Object.entries(allSAPortData).forEach(([portCode, portData]) => {
    if (!portData?.portCalls) return;

    portData.portCalls.forEach(portCall => {
      portCall.facilityCalls.forEach(facilityCall => {
        const transport = facilityCall.transport;
        const voyageServiceCode = transport?.outboundService?.carrierServiceCode || transport?.inboundService?.carrierServiceCode;
        const voyageNumber = transport?.outboundService?.carrierVoyageNumber || transport?.inboundService?.carrierVoyageNumber;
        const vesselName = transport?.vessel?.vesselName;

        // Filter by the requested service code
        if (!vesselName || !voyageNumber) return;
        if (voyageServiceCode !== serviceCode) {
          // Debug: Log filtered out vessels
          if (vesselName.includes('SANTA') || vesselName.includes('MAERSK')) {
            console.log(`[transformPortCallsData] Filtered out ${vesselName} ${voyageNumber} - service code: ${voyageServiceCode} (requested: ${serviceCode})`);
          }
          return;
        }

        const vesselKey = `${vesselName}-${voyageNumber}`;

        // Debug: Log service code filtering
        if (vesselName.includes('SANTA') || vesselName.includes('MAERSK')) {
          console.log(`[transformPortCallsData] ${vesselKey}: serviceCode=${voyageServiceCode}, requested=${serviceCode}, included=${voyageServiceCode === serviceCode}`);
        }

        if (!vesselMap.has(vesselKey)) {
          vesselMap.set(vesselKey, {
            vessel: {
              name: vesselName,
              voyage: voyageNumber,
              code: transport?.carrierVesselCode,
              vesselIMONumber: transport?.carrierVesselIMONumber
            },
            saPortCalls: [],
            allTransportCalls: [] // Port-calls API doesn't provide full transport calls
          });
        }

        const vesselEntry = vesselMap.get(vesselKey);

        // Extract SA port call with timestamps
        // Extract from callSchedules array - this contains the actual arrival/departure times
        if (facilityCall.callSchedules && facilityCall.callSchedules.length > 0) {
          facilityCall.callSchedules.forEach(callSchedule => {
            const eventTypeCode = callSchedule.transportEventTypeCode; // 'ARRI' or 'DEPA'
            const eventClassifierCode = callSchedule.eventClassifierCode; // 'ACT' or 'EST'
            const dateTime = callSchedule.classifierDateTime;

            if (dateTime) {
              vesselEntry.saPortCalls.push({
                port: portCode,
                type: eventTypeCode,
                status: eventClassifierCode,
                dateTime: dateTime,
                transportCallSequence: 0
              });
            }
          });
        }
      });
    });
  });

  // Filter out vessels with no SA port calls
  const consolidatedVessels = Array.from(vesselMap.values());
  console.log(`[transformPortCallsData] Total vessels found: ${consolidatedVessels.length}`);

  // Log vessel details for debugging
  console.log('[transformPortCallsData] Vessel SA port calls (first 5):');
  consolidatedVessels.slice(0, 5).forEach(v => {
    const latestDate = v.saPortCalls?.length > 0
      ? v.saPortCalls.map(pc => ({ port: pc.port, type: pc.type, dateTime: pc.dateTime }))
        .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))[0]
      : null;
    console.log(`  ${v.vessel.name} ${v.vessel.voyage}: latest=${latestDate?.dateTime || 'none'}`);
  });

  // Filter out stale vessels
  const activeVessels = filterStaleVessels(consolidatedVessels, finalLoadCodes);
  console.log(`[transformPortCallsData] Active vessels after filtering: ${activeVessels.length}`);

  // Sort vessels by voyage number
  const sortedVessels = activeVessels.sort((a, b) => {
    const voyageNumA = parseInt(a.vessel.voyage?.match(/\d+/)?.[0] || '0', 10);
    const voyageNumB = parseInt(b.vessel.voyage?.match(/\d+/)?.[0] || '0', 10);
    if (voyageNumA !== voyageNumB) {
      return voyageNumA - voyageNumB;
    }
    // Sort by latest SA departure time
    const depA = a.saPortCalls?.filter(c => c.type === 'DEPA').pop();
    const depB = b.saPortCalls?.filter(c => c.type === 'DEPA').pop();
    const timeA = depA ? new Date(depA.dateTime) : new Date('9999');
    const timeB = depB ? new Date(depB.dateTime) : new Date('9999');
    return timeA - timeB;
  });

  console.log('[transformPortCallsData] Sorted vessels (first 10):');
  sortedVessels.slice(0, 10).forEach((v, idx) => {
    console.log(`  ${idx}: ${v.vessel.name} ${v.vessel.voyage}`);
    v.saPortCalls.forEach(pc => {
      console.log(`    ${pc.port} ${pc.type}: ${pc.dateTime} (status: ${pc.status})`);
    });
  });

  // Split into current and upcoming rotations
  const currentRotation = sortedVessels.slice(0, vesselsPerGrid);
  const upcomingRotation = sortedVessels.slice(vesselsPerGrid, vesselsPerGrid * 2);

  const result = {
    current: {
      vessels: currentRotation.map(v => standardizeVessel(v.vessel)),
      loadPorts: buildLoadPorts(currentRotation, finalLoadConfigs),
      dischargePorts: buildDischargePorts(currentRotation, finalDischargeConfigs)
    },
    upcoming: {
      vessels: upcomingRotation.map(v => standardizeVessel(v.vessel)),
      loadPorts: buildLoadPorts(upcomingRotation, finalLoadConfigs),
      dischargePorts: buildDischargePorts(upcomingRotation, finalDischargeConfigs)
    }
  };

  console.log('[transformPortCallsData] RESULT - Current vessels:', result.current.vessels.map(v => `${v.name} ${v.voyage}`));
  console.log('[transformPortCallsData] RESULT - Upcoming vessels:', result.upcoming.vessels.map(v => `${v.name} ${v.voyage}`));

  return result;
};

/**
 * Fetch service schedules using port-schedules endpoint.
 * More efficient - only 3 API calls instead of 10+ IMO queries.
 *
 * @param {string} serviceCode - Service code to filter by (e.g., '278' for SAECS)
 * @param {object} dateParams - Date parameters
 * @returns {Promise<object>} - Standardized schedule data
 */
export const fetchServiceScheduleByPorts = async (serviceCode, dateParams) => {
  console.log(`[DcsaApiClient] fetchServiceScheduleByPorts called with serviceCode: ${serviceCode}`);
  try {
    const params = dateParams || getDateRange();
    const portCodes = ['ZACPT', 'ZADUR', 'ZAPLZ']; // SA load ports
    const allPortData = [];

    // Query each SA port (3 API calls total)
    for (let i = 0; i < portCodes.length; i++) {
      const portCode = portCodes[i];
      console.log(`[DcsaApiClient] Fetching port-schedules for ${portCode} (${i + 1}/${portCodes.length})...`);

      // Use today's date for port-schedules query
      const today = new Date().toISOString().split('T')[0];
      const url = `${API_BASE_URL}/port-schedules?UNLocationCode=${portCode}&date=${today}`;

      const response = await rateLimitedFetch(url);
      const portData = Array.isArray(response) ? response : [];

      console.log(`[DcsaApiClient] ${portCode}: Got ${portData.length} locations`);
      allPortData.push({ portCode, data: portData });

      // Add delay between port queries
      if (i < portCodes.length - 1) {
        await delay(2000);
      }
    }

    // Transform port-schedules data to vessel-schedules format
    // Then use existing transformServiceScheduleData function
    const vesselSchedules = transformPortSchedulesToVesselSchedules(allPortData, serviceCode);

    const dischargePortConfigs = getDischargePortConfigs(serviceCode);
    const result = transformServiceScheduleData(vesselSchedules, serviceCode, 12, dischargePortConfigs);

    console.log(`[DcsaApiClient] Port-schedules result:`, result);
    return result;

  } catch (error) {
    console.error(`[DcsaApiClient] Error fetching port-schedules for service ${serviceCode}:`, error);
    throw error;
  }
};

/**
 * Transform port-schedules data to vessel-schedules format.
 * Port-schedules returns data in a different structure than vessel-schedules.
 *
 * @param {Array} portDataArray - Array of {portCode, data} from port-schedules endpoint
 * @param {string} serviceCode - Service code to filter by
 * @returns {Array} - Vessel schedules in vessel-schedules format
 */
const transformPortSchedulesToVesselSchedules = (portDataArray, serviceCode) => {
  const vesselMap = new Map();

  portDataArray.forEach(({ portCode, data }) => {
    data.forEach(locationData => {
      const location = locationData.location || {};
      const vesselSchedules = locationData.vesselSchedules || [];

      vesselSchedules.forEach(vs => {
        const vesselIMONumber = vs.vessel?.vesselIMONumber;
        const servicePartners = vs.servicePartners || [];

        // Find the service partner matching our service code
        const partner = servicePartners.find(sp => sp.carrierServiceCode === serviceCode);
        if (!partner) return;

        const exportVoyage = partner.carrierExportVoyageNumber;
        if (!exportVoyage || !exportVoyage.endsWith('N')) return; // Only export voyages

        const vesselKey = `${vesselIMONumber}-${exportVoyage}`;

        if (!vesselMap.has(vesselKey)) {
          vesselMap.set(vesselKey, {
            carrierServiceName: partner.carrierServiceName || serviceCode,
            carrierServiceCode: serviceCode,
            vesselSchedules: [{
              vessel: vs.vessel,
              transportCalls: [] // Will be populated below
            }]
          });
        }

        // Create transport call from port-schedule data
        const serviceSchedule = vesselMap.get(vesselKey);
        const vesselSchedule = serviceSchedule.vesselSchedules[0];

        // Add this port as a transport call
        vesselSchedule.transportCalls.push({
          location: {
            UNLocationCode: portCode,
            locationName: location.locationName,
            facilitySMDGCode: location.facilitySMDGCode
          },
          carrierImportVoyageNumber: partner.carrierImportVoyageNumber,
          carrierExportVoyageNumber: exportVoyage,
          timestamps: vs.timestamps || []
        });
      });
    });
  });

  return Array.from(vesselMap.values());
};

/**
 * Fetch service schedules by vessel IMO number.
 * Use this to fetch vessels not returned by service code query.
 *
 * @param {string} vesselIMONumber - Vessel IMO number (7-8 digits)
 * @param {object} dateParams - Date parameters
 * @returns {Promise<Array>} - Service schedules for this vessel
 */
export const fetchVesselScheduleByIMO = async (vesselIMONumber, dateParams) => {
  console.log(`[DcsaApiClient] Fetching by IMO: ${vesselIMONumber}`);
  try {
    const params = dateParams || getDateRange();
    // Don't filter by operator - let API return all schedules for this IMO
    const url = `${API_BASE_URL}/vessel-schedules?vesselIMONumber=${vesselIMONumber}&startDate=${params.startDate}&endDate=${params.endDate}`;

    const response = await rateLimitedFetch(url);
    const schedules = Array.isArray(response) ? response : (response?.serviceSchedules || []);

    console.log(`[DcsaApiClient] IMO ${vesselIMONumber}: Got ${schedules.length} schedules`);
    return schedules;
  } catch (error) {
    console.error(`[DcsaApiClient] Error fetching IMO ${vesselIMONumber}:`, error);
    return [];
  }
};

/**
 * Fetch service schedules by service code, then fill in missing vessels by IMO.
 * This ensures complete vessel coverage while minimizing API calls.
 *
 * @param {string} serviceCode - Maersk service code (e.g., '278' for SAECS)
 * @param {object} dateParams - Optional date parameters
 * @param {Array} expectedIMOs - Expected vessel IMO numbers for this service
 * @returns {Promise<object>} - Standardized schedule data
 */
export const fetchServiceSchedule = async (serviceCode, dateParams, expectedIMOs, vesselsPerGrid = 8) => {
  console.log(`[DcsaApiClient] fetchServiceSchedule called with serviceCode: ${serviceCode}, strategy: ${API_STRATEGY}`);

  // Use port-schedules approach if configured (fewer API calls, avoids rate limiting)
  if (API_STRATEGY === 'ports') {
    return fetchServiceScheduleByPorts(serviceCode, dateParams);
  }

  const cacheKey = `${serviceCode}-${dateParams?.startDate || ''}-${dateParams?.endDate || ''}`;

  // Check if there's already a pending request for this same data (prevents duplicate calls from React StrictMode)
  if (pendingRequests.has(cacheKey)) {
    console.log(`[DcsaApiClient] Using cached pending request for ${cacheKey}`);
    return pendingRequests.get(cacheKey);
  }

  console.log(`[DcsaApiClient] fetchServiceSchedule called with serviceCode: ${serviceCode}`);

  // Create the request promise and cache it before executing
  const requestPromise = (async () => {
    try {
      const params = dateParams || getDateRange();
      console.log(`[DcsaApiClient] Date params:`, params);

      // First query by service code - gets most vessels efficiently
      // Don't filter by operator to avoid filtering out ONE, HLC vessels
      const url = `${API_BASE_URL}/vessel-schedules?carrierServiceCode=${serviceCode}&startDate=${params.startDate}&endDate=${params.endDate}`;
      console.log(`[DcsaApiClient] Step 1: Querying service ${serviceCode}...`);

      const response = await rateLimitedFetch(url);
      let allSchedules = Array.isArray(response) ? response : (response?.serviceSchedules || []);

      console.log(`[DcsaApiClient] Step 1: Got ${allSchedules.length} service schedules`);

      // Find which vessels we got
      const foundIMOs = new Set();
      allSchedules.forEach(serviceSchedule => {
        const vesselSchedules = serviceSchedule?.vesselSchedules || [];
        vesselSchedules.forEach(vs => {
          const imo = vs?.vessel?.vesselIMONumber;
          if (imo) foundIMOs.add(imo);
        });
      });

      console.log(`[DcsaApiClient] Found IMOs from service query: [${Array.from(foundIMOs).join(', ')}]`);

      // If we have expected IMOs, fetch the missing ones
      if (expectedIMOs && Array.isArray(expectedIMOs)) {
        const missingIMOs = expectedIMOs.filter(imo => !foundIMOs.has(imo));
        console.log(`[DcsaApiClient] Step 2: Fetching ${missingIMOs.length} missing vessels by IMO: [${missingIMOs.join(', ')}]`);

        // Fetch missing vessels with delay to avoid rate limiting
        for (const imo of missingIMOs) {
          await delay(2000); // 2000ms delay between requests to avoid rate limiting
          const vesselSchedules = await fetchVesselScheduleByIMO(imo, params);
          allSchedules = allSchedules.concat(vesselSchedules);
        }

        console.log(`[DcsaApiClient] Step 2: Total schedules after IMO fetches: ${allSchedules.length}`);
      }

      const dischargePortConfigs = getDischargePortConfigs(serviceCode);
      const result = transformServiceScheduleData(allSchedules, serviceCode, vesselsPerGrid, dischargePortConfigs);

      console.log(`[DcsaApiClient] Final result:`, result);

      return result;

    } catch (error) {
      console.error(`[DcsaApiClient] Error fetching schedule for service ${serviceCode}:`, error);
      throw error;
    } finally {
      // Clean up cache after a delay
      setTimeout(() => {
        pendingRequests.delete(cacheKey);
      }, CACHE_TTL);
    }
  })();

  // Cache the promise before returning
  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

/**
 * Load port configurations for each service
 * Note: ZAPLZ (Port Elizabeth) has been replaced by ZAZBA (Coega) for SAECS
 * W7A still uses ZAPLZ (Port Elizabeth)
 */
const LOAD_PORT_CONFIGS = {
  SAECS: [
    { name: 'Coega', portCode: 'ZAZBA' },
    { name: 'Durban', portCode: 'ZADUR' },
    { name: 'Cape Town', portCode: 'ZACPT' }
  ],
  W7A: [
    { name: 'Port Elizabeth', portCode: 'ZAPLZ' }
  ],
  PROTEA: [
    { name: 'Durban', portCode: 'ZADUR' }
  ],
  SAFARI: [
    { name: 'Durban', portCode: 'ZADUR' }
  ],
  MESAWA: [
    { name: 'Cape Town', portCode: 'ZACPT' },
    { name: 'Coega', portCode: 'ZAZBA' }
  ]
};

const finalLoadCodes = {
  SAECS: ['ZAZBA', 'ZADUR', 'ZACPT'],
  W7A: ['ZAPLZ'],
  PROTEA: ['ZADUR'],
  SAFARI: ['ZADUR'],
  MESAWA: ['ZACPT', 'ZAZBA']
};

/**
 * Discharge port configurations for each service
 */
const DISCHARGE_PORT_CONFIGS = {
  SAECS: [
    { name: 'Algeciras - ML', portCode: 'ESALG', facilitySMDGCode: 'APM' },
    { name: 'London Gateway', portCode: 'GBLGP' },
    { name: 'Rotterdam', portCode: 'NLRTM' },
    { name: 'Bremerhaven', portCode: 'DEBRV' }
  ],
  W7A: [
    { name: 'Tangier Med 2', portCode: 'MATGM', facilitySMDGCode: 'MAPTM02' },  // MED transshipment
    { name: 'Tangiers', portCode: 'MATGM', facilitySMDGCode: 'MAPTMTM' },        // NWC transshipment
    { name: 'London Gateway', portCode: 'GBLGP' },                               // Final POD (NWC)
    { name: 'Rotterdam', portCode: 'NLRTM' }                                     // Final POD (NWC)
  ],
  PROTEA: [
    { name: 'Jebel Ali', portCode: 'AEJEA' },
    { name: 'Mundra', portCode: 'INMUN' },
    { name: 'Jawaharlal Nehru', portCode: 'INNSA' }
  ],
  SAFARI: [
    { name: 'Port Louis', portCode: 'MUPPU' },
    { name: 'Tanjung Pelepas', portCode: 'MYTPP' },
    { name: 'Hong Kong', portCode: 'HKHKG' },
    { name: 'Shekou', portCode: 'CNSHK' },
    { name: 'Shanghai', portCode: 'CNSHA' },
    { name: 'Ningbo', portCode: 'CNNGB' }
  ],
  MESAWA: [
    { name: 'Jebel Ali', portCode: 'AEJEA' },
    { name: 'Mundra', portCode: 'INMUN' },
    { name: 'Jawaharlal Nehru', portCode: 'INNSA' }
  ]
};

/**
 * Service code to service name mapping
 */
const SERVICE_CODE_TO_NAME = {
  '278': 'SAECS',
  '160': 'SAFARI',   // 160 returns SAFARI data
  '251': 'PROTEA',   // 251 returns PROTEA data
  'MW1': 'MESAWA'
};

/**
 * Get service name from service code
 * @param {string} serviceCode - Maersk service code (e.g., '278' for SAECS, 'W7A')
 * @returns {string} - Service name
 */
const getServiceName = (serviceCode) => {
  // Check numeric codes first
  if (SERVICE_CODE_TO_NAME[serviceCode]) {
    return SERVICE_CODE_TO_NAME[serviceCode];
  }
  // For string codes like 'W7A', use them directly as keys
  return serviceCode;
};

/**
 * Get discharge port configs for a service code
 * @param {string} serviceCode - Maersk service code (e.g., '278' for SAECS, 'W7A')
 * @returns {Array} - Discharge port configurations
 */
const getDischargePortConfigs = (serviceCode) => {
  const serviceName = getServiceName(serviceCode);
  return DISCHARGE_PORT_CONFIGS[serviceName] || [];
};

/**
 * Get load port configs for a service code
 * @param {string} serviceCode - Maersk service code
 * @returns {Array} - Load port configurations
 */
const getLoadPortConfigs = (serviceCode) => {
  const serviceName = getServiceName(serviceCode);
  return LOAD_PORT_CONFIGS[serviceName] || [];
};

/**
 * Get load port codes for a service code
 * @param {string} serviceCode - Maersk service code
 * @returns {Array} - Load port codes
 */
const getLoadPortCodes = (serviceCode) => {
  const serviceName = getServiceName(serviceCode);
  return finalLoadCodes[serviceName] || [];
};

/**
 * Fetch point-to-point routes from origin to destination ports
 * @param {string} originPort - Origin port UNLocationCode (e.g., 'ZAPLZ')
 * @param {Array} destinationPorts - Array of destination port UNLocationCodes (e.g., ['GBLGP', 'NLRTM'])
 * @param {object} dateParams - Date parameters
 * @returns {Promise<object>} - Map of destination port to routes data
 */
export const fetchPointToPointRoutes = async (originPort, destinationPorts, dateParams) => {
  console.log(`[DcsaApiClient] fetchPointToPointRoutes: ${originPort} -> [${destinationPorts.join(', ')}]`);

  const params = dateParams || getDateRange();
  const routesData = {};

  for (const destPort of destinationPorts) {
    const url = `${API_BASE_URL}/point-to-point-routes?placeOfReceipt=${originPort}&placeOfDelivery=${destPort}&startDate=${params.startDate}&endDate=${params.endDate}`;
    console.log(`[DcsaApiClient] Fetching URL: ${url}`);

    try {
      const response = await rateLimitedFetch(url);
      routesData[destPort] = Array.isArray(response) ? response : [];
      console.log(`[DcsaApiClient] Got ${routesData[destPort].length} routes for ${destPort}`);

      // Log first route structure for debugging
      if (routesData[destPort].length > 0) {
        console.log(`[DcsaApiClient] First route structure for ${destPort}:`, JSON.stringify(routesData[destPort][0], null, 2));
      }
    } catch (error) {
      console.error(`[DcsaApiClient] Error fetching ${originPort} -> ${destPort}:`, error);
      routesData[destPort] = [];
    }

    // Add delay between requests
    await delay(1500);
  }

  return routesData;
};

/**
 * Match vessels from service schedule with point-to-point routes
 * @param {Array} serviceVessels - Vessels from service schedule
 * @param {object} routesData - Point-to-point routes data
 * @returns {Array} - Vessels with matched route information
 */
export const matchVesselsWithRoutes = (serviceVessels, routesData) => {
  const vesselMap = new Map();

  // First, add all service vessels
  serviceVessels.forEach(vessel => {
    const key = `${vessel.vessel.name}-${vessel.vessel.voyage}`;
    vesselMap.set(key, {
      ...vessel,
      routeDates: {}
    });
  });

  // Then, match with routes data
  Object.entries(routesData).forEach(([destPort, routes]) => {
    routes.forEach(route => {
      const vesselIMONumber = route?.vessel?.vesselIMONumber;
      const voyageNumber = route?.exportVoyageNumber || route?.importVoyageNumber;

      if (vesselIMONumber && voyageNumber) {
        // Find matching vessel by IMO or voyage
        for (const [key, vessel] of vesselMap) {
          if (vessel.vessel.vesselIMONumber === vesselIMONumber ||
              vessel.vessel.voyage === voyageNumber) {
            // Extract dates from route legs
            const legs = route?.legs || [];
            legs.forEach(leg => {
              const portCode = leg?.location?.UNLocationCode;
              if (portCode) {
                vessel.routeDates[portCode] = {
                  eta: findBestTimestamp(leg?.timestamps, 'ARRI'),
                  etd: findBestTimestamp(leg?.timestamps, 'DEPA')
                };
              }
            });
            vesselMap.set(key, vessel);
          }
        }
      }
    });
  });

  return Array.from(vesselMap.values());
};

/**
 * Merge route dates into W7A discharge ports
 * @param {object} baseSchedule - Base schedule from fetchServiceSchedule
 * @param {object} routesData - Point-to-point routes data
 * @returns {object} - Schedule with merged discharge port dates
 */
const mergeW7ARouteDates = (baseSchedule, routesData) => {
  console.log('[mergeW7ARouteDates] Starting merge with routesData:', Object.keys(routesData));
  const dischargePortConfigs = getDischargePortConfigs('W7A');
  console.log('[mergeW7ARouteDates] Discharge port configs:', dischargePortConfigs);
  const mergedSchedule = JSON.parse(JSON.stringify(baseSchedule)); // Deep clone

  // Create a map of vessels by IMO and voyage for easier matching
  const vesselMap = new Map();
  mergedSchedule.current.vessels.forEach((v, idx) => {
    vesselMap.set(`${v.imo}-${v.voyage}`, { vessel: v, index: idx });
  });
  console.log('[mergeW7ARouteDates] Vessel map created with', vesselMap.size, 'vessels');

  let matchCount = 0;
  let updateCount = 0;

  // Process each destination's routes
  Object.entries(routesData).forEach(([destPort, routes]) => {
    if (!Array.isArray(routes)) return;
    console.log(`[mergeW7ARouteDates] Processing ${destPort} with ${routes.length} routes`);

    routes.forEach((route, routeIdx) => {
      const vesselIMONumber = route?.vessel?.vesselIMONumber;
      const exportVoyageNumber = route?.exportVoyageNumber;
      const importVoyageNumber = route?.importVoyageNumber;

      console.log(`[mergeW7ARouteDates] Route ${routeIdx}: IMO=${vesselIMONumber}, exportVoyage=${exportVoyageNumber}, importVoyage=${importVoyageNumber}`);

      // Find matching vessel
      for (const [key, { vessel, index }] of vesselMap) {
        if (vessel.imo === vesselIMONumber ||
            vessel.voyage === exportVoyageNumber ||
            vessel.voyage === importVoyageNumber) {

          matchCount++;
          console.log(`[mergeW7ARouteDates] ✓ Matched vessel: ${vessel.name} (${vessel.imo}-${vessel.voyage})`);

          // Extract dates from all route legs (including transshipment ports)
          const legs = route?.legs || [];
          console.log(`[mergeW7ARouteDates] Route has ${legs.length} legs`);

          legs.forEach((leg, legIdx) => {
            const portCode = leg?.location?.UNLocationCode;
            const facilityCode = leg?.location?.facilitySMDGCode;
            console.log(`[mergeW7ARouteDates]   Leg ${legIdx}: portCode=${portCode}, facilityCode=${facilityCode}`);

            // Find matching discharge port config
            const portConfig = dischargePortConfigs.find(p =>
              p.portCode === portCode ||
              p.facilitySMDGCode === facilityCode
            );

            if (portConfig) {
              console.log(`[mergeW7ARouteDates]   ✓ Found port config: ${portConfig.name}`);
              // Find the corresponding discharge port in the schedule
              const dischargePort = mergedSchedule.current.dischargePorts.find(dp =>
                dp.portCode === portCode ||
                (dp.facilitySMDGCode && dp.facilitySMDGCode === facilityCode) ||
                dp.name === portConfig.name
              );

              if (dischargePort) {
                // Update the schedule for this vessel at this port
                const scheduleIndex = dischargePort.schedules.findIndex(s =>
                  s.vesselIndex === index
                );

                if (scheduleIndex >= 0) {
                  const arr = findBestTimestamp(leg?.timestamps, 'ARRI');
                  const dep = findBestTimestamp(leg?.timestamps, 'DEPA');

                  if (arr || dep) {
                    updateCount++;
                    console.log(`[mergeW7ARouteDates]   ✓ Updating ${portConfig.name} schedule for vessel ${vessel.name}`);
                    dischargePort.schedules[scheduleIndex] = {
                      ...dischargePort.schedules[scheduleIndex],
                      eta: formatDate(arr?.eventDateTime, arr?.eventClassifierCode),
                      etd: formatDate(dep?.eventDateTime, dep?.eventClassifierCode),
                      etaStatus: arr?.eventClassifierCode || '',
                      etdStatus: dep?.eventClassifierCode || ''
                    };
                  }
                }
              } else {
                console.log(`[mergeW7ARouteDates]   ✗ Discharge port not found in schedule`);
              }
            } else {
              console.log(`[mergeW7ARouteDates]   ✗ No matching port config for ${portCode}/${facilityCode}`);
            }
          });

          break;
        }
      }
    });
  });

  return mergedSchedule;
};

/**
 * Fetch W7A service schedule with port-to-point routes for transshipment tracking
 * W7A is a transshipment service: ZAPLZ -> Tangier (transship) -> Final PODs (LGP, RTM)
 *
 * @param {string} serviceCode - Service code ('W7A')
 * @param {object} dateParams - Date parameters
 * @param {Array} expectedIMOs - Expected vessel IMOs
 * @returns {Promise<object>} - Standardized schedule data with transshipment and POD dates
 */
/**
 * Extract transshipment and POD dates from vessel schedules
 * @param {Array} vesselSchedules - Vessel schedule data from API
 * @param {string} targetVoyage - Target voyage number (e.g., "617N")
 * @param {Array} dischargePortConfigs - Discharge port configurations
 * @returns {object} - Map of port codes to dates
 */
const extractPortDatesFromVesselSchedule = (vesselSchedules, targetVoyage, dischargePortConfigs) => {
  const portDates = {};

  vesselSchedules.forEach(vs => {
    const transportCalls = vs?.transportCalls || [];
    const dischargePortCodes = dischargePortConfigs.map(p => p.portCode);

    // Find all relevant transport calls for this voyage
    transportCalls.forEach(tc => {
      const portCode = tc?.location?.UNLocationCode;
      const facilityCode = tc?.location?.facilitySMDGCode;
      const exportVoyage = tc?.carrierExportVoyageNumber;
      const importVoyage = tc?.carrierImportVoyageNumber;

      // Check if this port is a discharge port we care about
      const portConfig = dischargePortConfigs.find(p =>
        p.portCode === portCode ||
        p.facilitySMDGCode === facilityCode
      );

      if (portConfig) {
        // Include if exportVoyage matches target, or if importVoyage matches (for return leg ports)
        if (exportVoyage === targetVoyage || importVoyage === targetVoyage) {
          const arr = findBestTimestamp(tc?.timestamps, 'ARRI');
          const dep = findBestTimestamp(tc?.timestamps, 'DEPA');

          if (arr || dep) {
            const key = portCode || facilityCode;
            if (!portDates[key]) {
              portDates[key] = {};
            }
            portDates[key] = {
              eta: formatDate(arr?.eventDateTime, arr?.eventClassifierCode),
              etd: formatDate(dep?.eventDateTime, dep?.eventClassifierCode),
              etaStatus: arr?.eventClassifierCode || '',
              etdStatus: dep?.eventClassifierCode || ''
            };
          }
        }
      }
    });
  });

  return portDates;
};

/**
 * Fetch W7A service schedule with complete vessel schedules for transshipment tracking
 * W7A is a transshipment service: ZAPLZ -> Tangier (transship) -> Final PODs (LGP, RTM)
 *
 * @param {string} serviceCode - Service code ('W7A')
 * @param {object} dateParams - Date parameters
 * @param {Array} expectedIMOs - Expected vessel IMOs
 * @returns {Promise<object>} - Standardized schedule data with transshipment and POD dates
 */
export const fetchW7AScheduleWithRoutes = async (serviceCode = 'W7A', dateParams, expectedIMOs) => {
  console.log(`[DcsaApiClient] fetchW7AScheduleWithRoutes called for ${serviceCode}`);

  try {
    const params = dateParams || getDateRange();
    const dischargePortConfigs = getDischargePortConfigs('W7A');
    console.log(`[DcsaApiClient] W7A discharge ports:`, dischargePortConfigs.map(p => `${p.name} (${p.portCode})`));

    // Step 1: Fetch base service schedule (ZAPLZ departure dates)
    const baseSchedule = await fetchServiceSchedule(serviceCode, params, expectedIMOs, 8);
    console.log(`[DcsaApiClient] Base schedule fetched: ${baseSchedule.current.vessels.length} vessels`);

    // Step 2: For each vessel, fetch complete schedule by IMO to get transshipment dates
    const mergedSchedule = JSON.parse(JSON.stringify(baseSchedule)); // Deep clone
    let updatedCount = 0;

    for (const vessel of mergedSchedule.current.vessels) {
      if (!vessel.imo) continue;

      console.log(`[DcsaApiClient] Fetching complete schedule for ${vessel.name} (IMO: ${vessel.imo}, Voyage: ${vessel.voyage})`);

      // Fetch complete vessel schedule
      const vesselSchedules = await fetchVesselScheduleByIMO(vessel.imo, params);

      // Extract dates for all discharge ports
      const portDates = extractPortDatesFromVesselSchedule(vesselSchedules, vessel.voyage, dischargePortConfigs);
      console.log(`[DcsaApiClient] Port dates extracted for ${vessel.name}:`, Object.keys(portDates));

      // Update each discharge port with the extracted dates
      Object.entries(portDates).forEach(([portKey, dates]) => {
        const portConfig = dischargePortConfigs.find(p =>
          p.portCode === portKey ||
          p.facilitySMDGCode === portKey
        );

        if (portConfig) {
          const dischargePort = mergedSchedule.current.dischargePorts.find(dp =>
            dp.portCode === portKey ||
            (dp.facilitySMDGCode && dp.facilitySMDGCode === portKey) ||
            dp.name === portConfig.name
          );

          if (dischargePort) {
            const vesselIndex = mergedSchedule.current.vessels.indexOf(vessel);
            const scheduleIndex = dischargePort.schedules.findIndex(s =>
              s.vesselIndex === vesselIndex
            );

            if (scheduleIndex >= 0) {
              dischargePort.schedules[scheduleIndex] = {
                ...dischargePort.schedules[scheduleIndex],
                ...dates
              };
              updatedCount++;
              console.log(`[DcsaApiClient] ✓ Updated ${portConfig.name} for ${vessel.name}`);
            }
          }
        }
      });

      // Small delay between IMO requests to avoid rate limiting
      await delay(500);
    }

    console.log(`[DcsaApiClient] W7A schedule complete: ${updatedCount} port updates`);
    return mergedSchedule;

  } catch (error) {
    console.error(`[DcsaApiClient] Error fetching W7A schedule with routes:`, error);
    throw error;
  }
};
